import { useMemo, useState } from 'react';
import AppLayout from '../components/AppLayout';
import { useSettings } from '../hooks/useSettings';
import { useLedgerData } from '../hooks/useLedgerData';
import { fmtMoney } from '../utils/currency';
import { balanceSheet, todayISO, type BSLine } from '../utils/ledgerReports';

function Section({ title, lines, total, money }: {
  title: string; lines: BSLine[]; total: number; money: (v: number) => string;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--text-3)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
      }}>{title}</div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        {lines.length === 0 && (
          <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-3)' }}>Nothing yet.</div>
        )}
        {lines.map(l => (
          <div key={l.accountId} style={{
            display: 'flex', justifyContent: 'space-between', padding: '9px 14px',
            borderBottom: '1px solid var(--border-2)', fontSize: 12,
          }}>
            <span style={{ color: 'var(--text-2)' }}>{l.name}</span>
            <span style={{ fontFamily: 'var(--font-num)', color: 'var(--text-1)' }}>{money(l.amount)}</span>
          </div>
        ))}
        <div style={{
          display: 'flex', justifyContent: 'space-between', padding: '10px 14px',
          background: 'var(--surface-2)', fontSize: 12, fontWeight: 700,
        }}>
          <span>Total {title}</span>
          <span style={{ fontFamily: 'var(--font-num)' }}>{money(total)}</span>
        </div>
      </div>
    </div>
  );
}

export default function BalanceSheetPage() {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const { entries, accounts, loading } = useLedgerData();
  const [asOf, setAsOf] = useState(todayISO());

  // balanceSheet uses a half-open window [.. , to), so add a day to make the
  // picked date inclusive.
  const toExclusive = useMemo(() => {
    const d = new Date(asOf + 'T00:00:00'); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, [asOf]);

  const bs = useMemo(() => balanceSheet(entries, accounts, toExclusive), [entries, accounts, toExclusive]);
  const hasData = bs.assets.length + bs.liabilities.length + bs.equity.length > 0 || Math.abs(bs.currentEarnings) > 0.005;

  const equityLines: BSLine[] = [
    ...bs.equity,
    { accountId: '__earnings', code: '3950', name: 'Current period earnings', amount: bs.currentEarnings },
  ];

  return (
    <AppLayout
      title="Balance Sheet"
      subtitle="What the business owns and owes"
      toolbar={
        <label className="tb-field">
          <span className="tb-field-label">As of</span>
          <input type="date" value={asOf} max={todayISO()} onChange={e => setAsOf(e.target.value)} />
        </label>
      }
    >
      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 260, borderRadius: 8 }} /></div>
      ) : !hasData ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Nothing recorded yet</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Record activity — or run <strong>Settings → Accounting → Rebuild Now</strong>.
          </div>
        </div>
      ) : (
        <>
          {/* Same compact stat-chip strip as Purchase/Sales' own summary
              rows (label · divider · value) — replaces the old run-on
              "Balanced — owns X, funded by Y, Z equity" sentence with a
              proper glanceable section. */}
          <div className="no-print" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { label: 'Business Owns', val: money(bs.totalAssets) },
              { label: 'Funded (Owed)', val: money(bs.totalLiabilities) },
              { label: 'Equity',        val: money(bs.totalEquity) },
            ].map(({ label, val }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)', padding: '6px 12px',
                boxShadow: 'var(--shadow-xs)', fontSize: 12,
              }}>
                <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>{label}</span>
                <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />
                <span style={{ color: 'var(--text-1)', fontWeight: 700, fontFamily: 'var(--font-num)' }}>{val}</span>
              </div>
            ))}
          </div>
          {!bs.balanced && (
            <div className="error-msg" style={{ marginBottom: 14 }}>
              Does not balance (owns {money(bs.totalAssets)} vs owes + equity {money(bs.totalLiabilities + bs.totalEquity)}) — please report this.
            </div>
          )}
          <div className="card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <Section title="Assets" lines={bs.assets} total={bs.totalAssets} money={money} />
            </div>
            <div>
              <Section title="Liabilities" lines={bs.liabilities} total={bs.totalLiabilities} money={money} />
              <Section title="Equity" lines={equityLines} total={bs.totalEquity} money={money} />
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
